import type { SlideFile } from './deck'
import type { ShelfFile } from '@/entities/shelfFile'
import type { Company } from '@/entities/company'

export interface Site {
  id: number
  file: SlideFile
  hotspots: Array<Hotspot>
  initial_pan_x: number
  initial_pan_y: number
  media_url: string
  height: number
  width: number
  title: string | null
  attribution: string | null
  updated_at: string
}

export type SiteCamera = {
  site_id: number
  camera_id: number | null
  pan_x: number // radians
  pan_y: number // radians
  zoom: number
  coordinate?: { x: number; y: number }
  hidden_hotspot_ids?: (number | undefined)[]
}

export type SiteEditMode = '' | 'single' | 'multi'

// M=Map P=PDF I=Photo V=Video N=Info L=Hotspot 3=Model
// NOTE: 'L' and '3' are deprecated
export type HotspotType = 'M' | 'P' | 'I' | 'V' | 'N' | 'L' | '3'
export type MeetingHotspotName = 'model' | 'info' | 'image' | 'location' | 'map' | 'pdf' | 'video'

export const HotspotTypeMap: Record<HotspotType, MeetingHotspotName> = {
  3: 'model',
  N: 'info',
  I: 'image',
  L: 'location',
  M: 'map',
  P: 'pdf',
  V: 'video',
}

export const MeetingHotspotNameMap: Record<MeetingHotspotName | 'site', HotspotType> = {
  model: '3',
  info: 'N',
  image: 'I',
  location: 'L',
  site: 'L',
  map: 'M',
  pdf: 'P',
  video: 'V',
}

export interface HotspotMedia {
  type: HotspotType
  id: number
  linked_to: number | null
  url: string | null
}

export interface SelectedHotspot {
  type: HotspotType
  dataId: number
  siteId?: number
  lookAt?: Origin
  zoom?: number
}

export interface Hotspot {
  id: number
  pos_x: number
  pos_y: number
  title: string
  status: 'P' | 'U' // P = public, U = private ?
  is_primary: boolean
  y_offset: number
  media: HotspotMedia[]
  content: string | null
  hotspot_id?: number
  site_id: number | null
}

export interface SiteChangeEvent {
  from_site_id?: number
  site_id?: number
  model_id?: number
  media_id?: number
  direction?: 'forward' | 'backward'
  look_at?: Origin
  zoom?: number
}

export interface SiteCameraChangeEvent {
  event: 'pan' | 'zoom'
  payload: any
}

export type Origin = {
  x: number
  y: number
}

type Size = {
  width: number
  height: number
}

export interface PdfCoordinates {
  origin: Origin
  size: Size
}

export interface SiteSubscriber {
  pan(payload: { coordinate: { x: number; y: number }; duration: number }): void
  zoom(payload: { zoom: number }): void
}

export interface Project {
  id: number
  project_image?: string
  created_at: string
  updated_at: string
  deleted_at: string
  title: string
  description: string
  longitude: number
  latitude: number
  url_slug: string
  is_featured: boolean
  view_access_type: 'private' | 'public' | 'selected'
  edit_access_type: 'private' | 'selected'
  file_id: number | null
  file?: ShelfFile
  is_paid: string
  sites: Site[]
  companies: Company[]
  thumbnail: string
  latest_content: string
  permissions: string
  allow_internal_access: boolean
}
